import {
  CaretLeftIcon,
  CaretRightIcon,
  DotsThreeVerticalIcon,
  WarningIcon,
  CaretUpIcon,
  CaretDownIcon,
  CaretUpDownIcon,
  Icon
} from '@phosphor-icons/react';
import { useState, useEffect, useRef, useLayoutEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import Checkbox from './Checkbox';
import useIsMobile from 'src/hooks/useIsMobile';

const ACTION_ICON_SIZE = 16;

export interface Column<T> {
  key: string;
  header: React.ReactNode;
  render?: (item: T) => React.ReactNode;
  align?: 'left' | 'center' | 'right';
  width?: string; // Flexible width specification (e.g., '1fr', '200px', 'minmax(150px, 1fr)')
  sortable?: boolean;
  sortAccessor?: (item: T) => string | number | Date | null | undefined;
}

export interface Action<T> {
  label: React.ReactNode | ((item: T) => React.ReactNode);
  icon: Icon;
  onClick: (item: T) => void;
  className?: string;
  disabled?: (item: T) => boolean;
  disabledTitle?: string | ((item: T) => string);
}

interface DataTableProps<T> {
  data: T[];
  columns: Column<T>[];
  onRowClick?: (item: T) => void;
  actions?: Action<T>[];
  itemsPerPage?: number;
  inlineActions?: boolean;
  onSelectionChange?: (selectedIds: (string | number)[]) => void;
  multiselect?: boolean;
  clearSelection?: boolean;
  error?: string | null;
  rowTitle?: (item: T) => string;
  enableSorting?: boolean;
  defaultSort?: { key: string; direction: 'asc' | 'desc' };
}

// Extracted Pagination component
const Pagination = ({
  currentPage,
  totalPages,
  totalItems,
  itemsPerPage,
  onPageChange
}: {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
  onPageChange: (page: number) => void;
}) => {
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, totalItems);

  const getPaginationRange = (currentPage: number, totalPages: number): (number | string)[] => {
    const delta = 0;
    const range: (number | string)[] = [];

    for (let i = 1; i <= totalPages; i++) {
      if (i === 1 || i === totalPages || (i >= currentPage - delta && i <= currentPage + delta)) {
        range.push(i);
      } else if (i === currentPage - delta - 1 || i === currentPage + delta + 1) {
        range.push('...');
      }
    }

    return range;
  };

  return (
    <div className="flex pl-1 py-2 text-sm items-center justify-between border-b-2 border-border-subtle">
      <div className="flex items-center">
        <p className="text-sm text-content-default">
          {startIndex + 1}-{endIndex} of {totalItems} items
        </p>
      </div>

      <div className="flex items-center gap-1 text-content-subtle text-xs">
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="hover:bg-surface-hover w-[26px] h-[26px] flex items-center justify-center rounded-md p-1"
        >
          <CaretLeftIcon size={16} />
        </button>
        <div className="flex items-center space-x-1">
          {getPaginationRange(currentPage, totalPages).map((page, index) =>
            page === '...' ? (
              <span key={`ellipsis-${index}`} className="px-1 text-content-subtle">
                ...
              </span>
            ) : (
              <button
                key={page}
                onClick={() => onPageChange(page as number)}
                className={`w-[26px] h-[26px] flex items-center justify-center rounded-md text-xs font-medium ${
                  currentPage === page
                    ? 'bg-secondary-blue text-primary-lightblue'
                    : 'text-content-subtle hover:bg-surface-hover'
                }`}
              >
                {page}
              </button>
            )
          )}
        </div>
        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="hover:bg-surface-hover w-[26px] h-[26px] flex items-center justify-center rounded-md p-1"
        >
          <CaretRightIcon size={16} />
        </button>
      </div>
    </div>
  );
};

// Extracted ActionMenu component
const ActionMenu = <T,>({
  item,
  actions,
  menuRef,
  onClose,
  style,
  onSize
}: {
  item: T;
  actions: Action<T>[];
  menuRef: React.RefObject<HTMLDivElement | null>;
  onClose: () => void;
  style?: React.CSSProperties;
  onSize?: (size: { width: number; height: number }) => void;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    if (ref.current && onSize) {
      const rect = ref.current.getBoundingClientRect();
      onSize({ width: rect.width, height: rect.height });
    }
  }, []);
  return (
    <div
      ref={(el) => {
        ref.current = el;
        if (menuRef) menuRef.current = el;
      }}
      style={style}
      className={`rounded-md shadow-md bg-white ring-1 ring-border-default z-10`}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="py-1" role="menu">
        {actions.map((action, index) => {
          const isDisabled = action.disabled ? action.disabled(item) : false;
          const ActionIcon = action.icon;
          return (
            <button
              key={index}
              onClick={() => {
                if (isDisabled) return;
                action.onClick(item);
                onClose();
              }}
              disabled={isDisabled}
              title={
                isDisabled
                  ? typeof action.disabledTitle === 'function'
                    ? action.disabledTitle(item)
                    : action.disabledTitle || undefined
                  : undefined
              }
              className={`w-full px-4 py-2 text-sm text-left flex items-center gap-2 ${
                isDisabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-bluegray-100'
              } ${action.className || 'text-content-default'}`}
              role="menuitem"
            >
              <ActionIcon size={ACTION_ICON_SIZE} />
              {typeof action.label === 'function' ? action.label(item) : action.label}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default function DataTable<T extends { id: string | number; icon?: React.ReactNode }>({
  data,
  columns,
  onRowClick,
  actions,
  itemsPerPage = 10,
  inlineActions = false,
  onSelectionChange,
  multiselect = false,
  clearSelection = false,
  error,
  rowTitle,
  enableSorting = true,
  defaultSort
}: DataTableProps<T>) {
  const [currentPage, setCurrentPage] = useState(1);
  const [activeMenu, setActiveMenu] = useState<string | number | null>(null);
  const [menuCoords, setMenuCoords] = useState<{ right: number; bottom: number } | null>(null);
  const [menuSize, setMenuSize] = useState<{ width: number; height: number } | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [selectedIds, setSelectedIds] = useState<(string | number)[]>([]);
  const [hoveredRow, setHoveredRow] = useState<string | number | null>(null);
  const tableRef = useRef<HTMLTableElement>(null);
  const [sortState, setSortState] = useState<{ key: string | null; direction: 'asc' | 'desc' }>(() =>
    defaultSort ? { key: defaultSort.key, direction: defaultSort.direction } : { key: null, direction: 'asc' }
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setActiveMenu(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    onSelectionChange?.(selectedIds);
  }, [selectedIds, onSelectionChange]);

  useEffect(() => {
    if (clearSelection) {
      setSelectedIds([]);
      onSelectionChange?.([]);
    }
  }, [clearSelection, onSelectionChange]);

  const handleRowAction = (item: T, event: React.MouseEvent<HTMLButtonElement>) => {
    const button = event.currentTarget;
    const rect = button.getBoundingClientRect();
    setMenuCoords({ right: rect.right + window.scrollX, bottom: rect.bottom + window.scrollY });
    setActiveMenu(activeMenu === item.id ? null : item.id);
    setMenuSize(null);
  };

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation();
    if (selectedIds.length === data.length) {
      setSelectedIds([]);
      onSelectionChange?.([]);
    } else {
      const allIds = data.map((item) => item.id);
      setSelectedIds(allIds);
      onSelectionChange?.(allIds);
    }
  };

  const handleSelectRow = (id: string | number, e: React.ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation();
    const newSelectedIds = selectedIds.includes(id)
      ? selectedIds.filter((selectedId) => selectedId !== id)
      : [...selectedIds, id];
    setSelectedIds(newSelectedIds);
    onSelectionChange?.(newSelectedIds);
  };

  const getColumnByKey = (key: string | null) => (key ? columns.find((c) => c.key === key) : undefined);

  const sortedData = useMemo(() => {
    if (!enableSorting || !sortState.key) return data;
    const column = getColumnByKey(sortState.key);
    if (!column) return data;
    const sortable = column.sortable !== false; // default sortable unless explicitly false
    if (!sortable) return data;

    const getValue = (item: T): any => {
      if (column.sortAccessor) return column.sortAccessor(item);
      const raw = (item as any)[column.key];
      return raw;
    };

    const compare = (a: T, b: T) => {
      const va = getValue(a);
      const vb = getValue(b);

      if (va == null && vb == null) return 0;
      if (va == null) return -1;
      if (vb == null) return 1;

      if (typeof va === 'number' && typeof vb === 'number') {
        return va - vb;
      }

      const aTime = va instanceof Date ? va.getTime() : NaN;
      const bTime = vb instanceof Date ? vb.getTime() : NaN;
      if (!Number.isNaN(aTime) && !Number.isNaN(bTime)) {
        return aTime - bTime;
      }

      const as = String(va).toLowerCase();
      const bs = String(vb).toLowerCase();
      return as.localeCompare(bs);
    };

    const dir = sortState.direction === 'asc' ? 1 : -1;
    const cloned = [...data];
    cloned.sort((a, b) => dir * compare(a, b));
    return cloned;
  }, [data, columns, sortState, enableSorting]);

  const sourceData = enableSorting ? sortedData : data;
  const totalPages = Math.ceil(sourceData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, sourceData.length);
  const paginatedData = sourceData.slice(startIndex, endIndex);
  const isMobile = useIsMobile();

  // Clamp current page when data size changes to avoid state updates during render
  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(1);
    }
  }, [currentPage, totalPages]);

  return (
    <div className="space-y-4 overflow-x-auto">
      <table ref={tableRef} className="w-full min-w-0" style={{ tableLayout: 'fixed' }}>
        <thead>
          <tr className="border-t-2 border-b-2 border-border-subtle">
            {(multiselect || data.some((item) => item.icon)) && (
              <th className="w-[48px] h-[48px]">
                <div className="flex items-center justify-center">
                  {multiselect ? (
                    <Checkbox
                      checked={selectedIds.length === data.length}
                      onChange={handleSelectAll}
                      className="w-[48px] h-[48px] flex items-center justify-center"
                    />
                  ) : (
                    <div className="w-[48px] h-[48px]"></div>
                  )}
                </div>
              </th>
            )}
            {columns.map((column, index) => {
              const isSortable = enableSorting && column.sortable !== false;
              const isActive = sortState.key === column.key;
              const direction = sortState.direction;
              return (
                <th
                  key={column.key}
                  className={`px-4 py-3 text-left text-sm font-medium min-w-0 ${isSortable ? 'cursor-pointer select-none' : ''}`}
                  style={{ width: column.width || 'auto' }}
                  onClick={() => {
                    if (!isSortable) return;
                    setCurrentPage(1);
                    setSortState((prev) => {
                      if (prev.key === column.key) {
                        return { key: column.key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
                      }
                      return { key: column.key, direction: 'asc' };
                    });
                  }}
                >
                  <div
                    className={`${index > 0 ? 'border-l border-border-subtle pl-6' : 'pl-0'} min-w-0 truncate flex items-center gap-2 text-content-subtle`}
                  >
                    <span className="truncate">{column.header}</span>
                    {isSortable &&
                      (isActive ? (
                        direction === 'asc' ? (
                          <CaretUpIcon size={14} />
                        ) : (
                          <CaretDownIcon size={14} />
                        )
                      ) : (
                        <CaretUpDownIcon size={14} className="text-content-tertiary" />
                      ))}
                  </div>
                </th>
              );
            })}
            {actions && actions.length > 0 && <th className="w-[48px] h-[48px]"></th>}
          </tr>
        </thead>
        <tbody>
          {paginatedData.length === 0 ? (
            <tr>
              <td
                colSpan={
                  columns.length + (data[0]?.icon ? 1 : 0) + (actions ? (inlineActions ? actions.length : 1) : 0)
                }
                className="text-center text-content-subtle py-6"
              >
                {error ? (
                  <div className="flex flex-col items-center justify-center gap-2 text-misc-error">
                    <WarningIcon size={24} />
                    <span className="text-base font-medium">{error}</span>
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center gap-2">
                    <span className="text-base font-medium">No data available</span>
                    <span className="text-sm text-content-tertiary">
                      Try adjusting your filters or adding new items.
                    </span>
                  </div>
                )}
              </td>
            </tr>
          ) : (
            paginatedData.map((item, idx) => (
              <tr
                key={item.id}
                className={`data-table-row text-sm transition-colors border-b border-border-subtle ${onRowClick ? 'cursor-pointer hover:bg-bluegray-100 active:bg-bluegray-200' : ''}`}
                onClick={() => onRowClick?.(item)}
                onMouseEnter={() => setHoveredRow(item.id)}
                onMouseLeave={() => setHoveredRow(null)}
                title={rowTitle ? rowTitle(item) : undefined}
              >
                {(multiselect || item.icon) && (
                  <td className="w-[48px] flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
                    {multiselect &&
                      (selectedIds.includes(item.id) ? (
                        <Checkbox
                          checked={true}
                          onChange={(e) => handleSelectRow(item.id, e)}
                          className="w-[48px] h-[48px] flex items-center justify-center"
                        />
                      ) : isMobile || hoveredRow === item.id ? (
                        <Checkbox
                          checked={false}
                          onChange={(e) => handleSelectRow(item.id, e)}
                          className="w-[48px] h-[48px] flex items-center justify-center"
                        />
                      ) : (
                        <div className="w-[48px] h-[48px] flex items-center justify-center">{item.icon}</div>
                      ))}
                    {!multiselect && item.icon && (
                      <div className="w-[48px] h-[48px] flex items-center justify-center">{item.icon}</div>
                    )}
                  </td>
                )}

                {columns.map((column, index) => {
                  const cellValue = column.render ? column.render(item) : String(item[column.key as keyof T] || '');
                  // Extract text content for title attribute
                  const textValue =
                    typeof cellValue === 'string' ? cellValue : String(item[column.key as keyof T] || '');

                  return (
                    <td
                      key={column.key}
                      className={`${index === 0 ? 'pl-4' : ''} ${index > 0 ? 'pl-10' : ''} h-[48px] text-${column.align || 'left'} text-sm font-normal text-content-default min-w-0`}
                      style={{ width: column.width || 'auto' }}
                    >
                      <div className="min-w-0 truncate" title={textValue}>
                        {cellValue}
                      </div>
                    </td>
                  );
                })}
                {actions && (
                  <td className="w-[48px] h-[48px] relative" onClick={(e) => e.stopPropagation()}>
                    <button
                      onClick={(e) => handleRowAction(item, e)}
                      aria-label="Actions"
                      aria-haspopup="menu"
                      aria-expanded={activeMenu === item.id}
                      className="w-[32px] h-[32px] flex items-center rounded-md justify-center text-content-default hover:bg-bluegray-100 row-actions-trigger"
                    >
                      <DotsThreeVerticalIcon size={18} />
                    </button>

                    {activeMenu === item.id &&
                      menuCoords &&
                      menuSize === null &&
                      createPortal(
                        <ActionMenu
                          item={item}
                          actions={actions}
                          menuRef={menuRef}
                          onClose={() => setActiveMenu(null)}
                          style={{
                            position: 'absolute',
                            top: -9999,
                            left: -9999,
                            visibility: 'hidden',
                            zIndex: -1
                          }}
                          onSize={setMenuSize}
                        />,
                        document.body
                      )}

                    {activeMenu === item.id &&
                      menuCoords &&
                      menuSize &&
                      createPortal(
                        <ActionMenu
                          item={item}
                          actions={actions}
                          menuRef={menuRef}
                          onClose={() => setActiveMenu(null)}
                          style={{
                            position: 'absolute',
                            top: menuCoords.bottom - menuSize.height,
                            left: menuCoords.right - menuSize.width,
                            zIndex: 9999
                          }}
                          onSize={setMenuSize}
                        />,
                        document.body
                      )}
                  </td>
                )}
              </tr>
            ))
          )}
        </tbody>
      </table>

      {totalPages > 0 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={data.length}
          itemsPerPage={itemsPerPage}
          onPageChange={setCurrentPage}
        />
      )}
    </div>
  );
}
